/**
 * Berechnungsfunktionen für Urlaubsanspruch nach BUrlG
 */

import { subDays, parseISO, isWithinInterval, startOfDay, differenceInCalendarDays } from 'date-fns';
import type {
  DayEntry,
  CompanySettings,
  CalculationResults,
  Last13WeeksData,
  MonthlyData,
  SettingsSegment,
} from '../types';

const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
] as const;

function getMonthName(monthIndex: number): string {
  return MONTH_NAMES[monthIndex] ?? '';
}

function parseYMD(s: string): [number, number, number] {
  // Toleriere ISO-Timestamps ("2026-01-01T00:00:00.000Z") — nur YYYY-MM-DD-Präfix verwenden.
  const ymd = /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : s;
  const parts = ymd.split('-');
  if (parts.length !== 3) {
    throw new Error(`Ungültiges Datum: "${s}" (erwartet YYYY-MM-DD)`);
  }
  const [y, m, d] = parts.map(Number);
  if (y === undefined || m === undefined || d === undefined || Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    throw new Error(`Ungültiges Datum: "${s}"`);
  }
  return [y, m, d];
}

/**
 * Rundung nach §5 BUrlG:
 * Bruchteile von mindestens 0,5 werden auf volle Tage aufgerundet
 */
export function roundVacationDays(days: number): number {
  const fraction = days - Math.floor(days);
  return fraction >= 0.5 ? Math.ceil(days) : Math.floor(days);
}

/**
 * Findet das Referenzdatum für die 13-Wochen-Berechnung.
 * Dies ist der Beginn des letzten zusammenhängenden Urlaubsblocks.
 * Fallback auf das heutige Datum wenn kein Urlaub genommen wurde.
 */
export function findVacationReferenceDate(entries: Record<string, DayEntry>): Date {
  // Finde alle Urlaubstage (ganze und halbe), sortiert absteigend
  const vacationDates = Object.values(entries)
    .filter((e) => e.isVacation || e.isHalfVacation)
    .map((e) => e.date)
    .sort()
    .reverse();

  if (vacationDates.length === 0) {
    return new Date();
  }

  // Finde den Beginn des letzten zusammenhängenden Urlaubsblocks
  let firstDayOfBlock = parseISO(vacationDates[0]!);

  for (let i = 1; i < vacationDates.length; i++) {
    const currentDate = parseISO(vacationDates[i]!);
    const prevDate = parseISO(vacationDates[i - 1]!);
    const diff = differenceInCalendarDays(prevDate, currentDate);

    // Erlaubt Lücken von bis zu 2 Tagen (Wochenende)
    if (diff <= 3) {
      firstDayOfBlock = currentDate;
    } else {
      break;
    }
  }

  return firstDayOfBlock;
}

/**
 * Zählt die vollen Beschäftigungsmonate im betrachteten Jahr (§5 BUrlG).
 * Ein Monat zählt als "voll", wenn das Arbeitsverhältnis am 1. UND letzten Tag des Monats besteht.
 *
 * @returns 1–12 (Anzahl voller Monate)
 */
export function countFullEmploymentMonths(
  year: number,
  employmentStart?: string | null,
  employmentEnd?: string | null
): number {
  // Kein Beschäftigungszeitraum gesetzt → volles Jahr
  if (!employmentStart && !employmentEnd) return 12;

  // parseYMD toleriert ISO-Timestamps und vermeidet Timezone-Drift durch
  // parseISO (CEST: "2024-07-01T00:00:00.000Z" → 01.07. 02:00 lokal).
  let startDate: Date | null = null;
  if (employmentStart) {
    const [sy, sm, sd] = parseYMD(employmentStart);
    startDate = new Date(sy, sm - 1, sd);
  }
  let endDate: Date | null = null;
  if (employmentEnd) {
    const [ey, em, ed] = parseYMD(employmentEnd);
    endDate = new Date(ey, em - 1, ed);
  }

  // Beschäftigung umfasst das ganze Jahr
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  if ((!startDate || startDate <= yearStart) && (!endDate || endDate >= yearEnd)) {
    return 12;
  }

  let fullMonths = 0;

  for (let month = 0; month < 12; month++) {
    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0); // Letzter Tag des Monats

    // Arbeitsverhältnis muss am 1. des Monats UND am letzten Tag des Monats bestehen
    const employedAtStart = !startDate || startDate <= firstOfMonth;
    const employedAtEnd = !endDate || endDate >= lastOfMonth;

    if (employedAtStart && employedAtEnd) {
      fullMonths++;
    }
  }

  return fullMonths;
}

/**
 * Zählt die vollen Kalendermonate die vollständig innerhalb des Zeitraums liegen.
 * Ein Monat ist "voll" wenn der gesamte Monat (1. bis letzter Tag) im Zeitraum enthalten ist.
 */
export function countFullMonths(from: Date, to: Date): number {
  let count = 0;
  const startYear = from.getFullYear();
  const startMonth = from.getMonth();
  const endYear = to.getFullYear();
  const endMonth = to.getMonth();

  for (let y = startYear; y <= endYear; y++) {
    const mStart = (y === startYear) ? startMonth : 0;
    const mEnd = (y === endYear) ? endMonth : 11;

    for (let m = mStart; m <= mEnd; m++) {
      const firstOfMonth = new Date(y, m, 1);
      const lastOfMonth = new Date(y, m + 1, 0); // Letzter Tag des Monats

      if (from <= firstOfMonth && to >= lastOfMonth) {
        count++;
      }
    }
  }

  return count;
}

/**
 * Berechnet den Urlaubsanspruch bei Mid-Year Settings-Wechsel
 * nach §5 BUrlG Teilurlaub-Prinzip (1/12 pro vollem Monat).
 */
export function calculateVacationEntitlementFromSegments(
  segments: SettingsSegment[],
  averageWorkDaysPerWeek: number
): number {
  let totalEntitlement = 0;

  for (const segment of segments) {
    // Lokale Zeitzone verwenden (nicht UTC), damit Vergleich mit new Date(y,m,d) in countFullMonths korrekt ist
    const [fy, fm, fd] = parseYMD(segment.from);
    const from = new Date(fy, fm - 1, fd);
    const [ty, tm, td] = parseYMD(segment.to);
    const to = new Date(ty, tm - 1, td);
    const fullMonths = countFullMonths(from, to);

    // Grundanspruch für dieses Segment
    const yearlyEntitlement =
      (segment.values.vacationDaysFullTime / segment.values.workDaysPerWeekFullTime) *
      averageWorkDaysPerWeek;

    // Anteil nach §5 BUrlG (Teilurlaub = 1/12 pro vollem Monat)
    const segmentEntitlement = yearlyEntitlement * fullMonths / 12;

    totalEntitlement += segmentEntitlement;
  }

  return roundVacationDays(totalEntitlement);
}

/**
 * Berechnet die bis zum Referenzdatum erdienten Urlaubstage (Pro-rata nach §5 BUrlG).
 * Berücksichtigt Beschäftigungszeitraum: nur volle Monate zählen.
 */
export function calculateEarnedVacationDays(
  yearlyEntitlement: number,
  referenceDate: Date,
  year: number,
  employmentStart?: string | null,
  employmentEnd?: string | null,
): number {
  if (yearlyEntitlement <= 0) return 0;

  // from: Max(1. Jan des Jahres, employmentStart)
  let from = new Date(year, 0, 1);
  if (employmentStart) {
    const [sy, sm, sd] = parseYMD(employmentStart);
    const empStart = new Date(sy, sm - 1, sd);
    if (empStart > from) from = empStart;
  }

  // to: Min(referenceDate, employmentEnd falls im aktuellen Jahr)
  let to = referenceDate;
  if (employmentEnd) {
    const [ey, em, ed] = parseYMD(employmentEnd);
    const empEnd = new Date(ey, em - 1, ed);
    if (empEnd < to) to = empEnd;
  }

  if (to < from) return 0;

  const fullMonths = countFullMonths(from, to);
  if (fullMonths >= 12) return yearlyEntitlement;

  return roundVacationDays(fullMonths / 12 * yearlyEntitlement);
}

/**
 * Berechnet erdiente Urlaubstage bei Mid-Year Settings-Wechsel (Segmente).
 * Wie calculateVacationEntitlementFromSegments(), aber nur bis zum Referenzdatum.
 */
export function calculateEarnedVacationDaysFromSegments(
  segments: SettingsSegment[],
  averageWorkDaysPerWeek: number,
  referenceDate: Date,
): number {
  let totalEntitlement = 0;

  for (const segment of segments) {
    const [fy, fm, fd] = parseYMD(segment.from);
    const segFrom = new Date(fy, fm - 1, fd);
    const [ty, tm, td] = parseYMD(segment.to);
    let segTo = new Date(ty, tm - 1, td);

    // Segment auf referenceDate beschneiden
    if (segTo > referenceDate) segTo = referenceDate;
    if (segFrom > referenceDate) continue;

    const fullMonths = countFullMonths(segFrom, segTo);

    const yearlyEntitlement =
      (segment.values.vacationDaysFullTime / segment.values.workDaysPerWeekFullTime) *
      averageWorkDaysPerWeek;

    totalEntitlement += yearlyEntitlement * fullMonths / 12;
  }

  return roundVacationDays(totalEntitlement);
}

/**
 * Findet den WageType aus dem zeitlich passenden SettingsSegment für ein Datum.
 * Spiegelt die Backend-Logik aus carryoverCalculation.ts (findSegmentForDate).
 */
export function findSegmentWageType(
  segments: SettingsSegment[],
  dateStr: string
): string | null | undefined {
  for (let i = segments.length - 1; i >= 0; i--) {
    const seg = segments[i]!;
    if (seg.from <= dateStr && seg.to >= dateStr) {
      return seg.values.wageType;
    }
  }
  return segments[0]?.values.wageType;
}

/**
 * Berechnet alle Kennzahlen aus den Tageseinträgen
 */
export function calculateResults(
  entries: Record<string, DayEntry>,
  settings: CompanySettings,
  _year: number,
  referenceDate: Date = new Date(),
  carryOverFromPreviousYear: number = 0,
  previousYearEntries?: Record<string, DayEntry>,
  vacationAdjustmentDays: number = 0,
  employmentStart?: string | null,
  employmentEnd?: string | null,
  settingsSegments?: SettingsSegment[]
): CalculationResults {
  const allEntries = Object.values(entries);

  // Grunddaten
  const totalHours = allEntries.reduce((sum, e) => sum + e.hours, 0);
  const workDays = allEntries.filter((e) => e.hours > 0).length;
  const holidays = allEntries.filter((e) => e.isHoliday).length;
  const vacationDaysTaken = allEntries.reduce((sum, e) => {
    // Betriebsferien: nur für FIXED-Employees als Urlaub zählen (§5 BUrlG)
    if (e.isCompanyVacation) {
      const wt = settingsSegments?.length
        ? findSegmentWageType(settingsSegments, e.date)
        : undefined;
      if (wt === 'FIXED') return sum + (e.isHalfVacation ? 0.5 : 1);
      if (wt !== undefined) return sum; // HOURLY → kein Abzug
      // Fallback (keine Segmente): altes Verhalten (isVacation zählen)
    }
    if (e.isVacation) return sum + 1;
    if (e.isHalfVacation) return sum + 0.5;
    return sum;
  }, 0);
  const sickDays = allEntries.filter((e) => e.isSickDay).length;

  // Durchschnitte — tatsächliche Zeitspanne statt festes 52
  const workEntries = allEntries.filter((e) => e.hours > 0);
  let weeksElapsed = 52; // Fallback

  if (workEntries.length >= 2) {
    const dates = workEntries.map((e) => e.date).sort();
    const firstDate = parseISO(dates[0]!);
    const lastDate = parseISO(dates[dates.length - 1]!);
    const daySpan = differenceInCalendarDays(lastDate, firstDate);
    weeksElapsed = Math.max(daySpan / 7, 1);
  } else if (workEntries.length === 1) {
    weeksElapsed = 1;
  }

  const averageHoursPerDay = workDays > 0 ? totalHours / workDays : 0;
  const averageWorkDaysPerWeek = workDays / weeksElapsed;

  // Urlaubsanspruch (Ganzjahresbasis)
  const vacationEntitlementRaw =
    (settings.vacationDaysFullTime / settings.workDaysPerWeekFullTime) *
    averageWorkDaysPerWeek;

  // Teilurlaub nach §5 BUrlG bei unterjährigem Ein-/Austritt
  const fullEmploymentMonths = countFullEmploymentMonths(_year, employmentStart, employmentEnd);
  const proRataFactor = fullEmploymentMonths / 12;

  // Pro-rata Urlaubsanspruch (wird auch im Return-Objekt gebraucht)
  const vacationEntitlementProRated = vacationEntitlementRaw * proRataFactor;

  // Segment-basierte Berechnung bei Mid-Year Settings-Wechsel
  let vacationEntitlementRounded: number;
  if (settingsSegments && settingsSegments.length > 1) {
    // Mehrere Segmente: Teilurlaub pro Segment nach §5 BUrlG
    vacationEntitlementRounded = calculateVacationEntitlementFromSegments(
      settingsSegments, averageWorkDaysPerWeek
    );
    // Zusätzlich Pro-rata bei unterjährigem Ein-/Austritt
    if (proRataFactor < 1) {
      vacationEntitlementRounded = roundVacationDays(vacationEntitlementRounded * proRataFactor);
    }
  } else {
    // Ein Segment oder keine Segmente: bisherige Berechnung
    vacationEntitlementRounded = roundVacationDays(vacationEntitlementProRated);
  }

  // Ganzjahresanspruch nur separat speichern wenn Teilurlaub greift
  const vacationEntitlementFullYear = proRataFactor < 1
    ? roundVacationDays(vacationEntitlementRaw)
    : undefined;

  // Pro-rata erdiente Tage bis referenceDate
  let vacationEarnedToDate: number;
  if (settingsSegments && settingsSegments.length > 1) {
    vacationEarnedToDate = calculateEarnedVacationDaysFromSegments(
      settingsSegments, averageWorkDaysPerWeek, referenceDate
    );
    if (proRataFactor < 1) {
      vacationEarnedToDate = roundVacationDays(vacationEarnedToDate * proRataFactor);
    }
  } else {
    vacationEarnedToDate = calculateEarnedVacationDays(
      vacationEntitlementRounded, referenceDate, _year, employmentStart, employmentEnd
    );
  }

  // Gesamter Urlaubsanspruch inkl. Übertrag + Anpassungen (Sonderurlaub, Bildungsurlaub, etc.)
  const totalVacationEntitlement = vacationEntitlementRounded + carryOverFromPreviousYear + vacationAdjustmentDays;
  const remainingVacation = totalVacationEntitlement - vacationDaysTaken;

  // 13-Wochen-Referenzdatum: Erster Tag des letzten Urlaubsblocks (oder today)
  const vacRefDate = findVacationReferenceDate(entries);
  const effectiveRefDate = vacRefDate || referenceDate;

  // 13-Wochen-Berechnung (mit Vorjahresdaten für korrekte Berechnung am Jahresanfang)
  const last13Weeks = calculateLast13Weeks(entries, settings, effectiveRefDate, previousYearEntries);

  return {
    totalHours: round(totalHours, 2),
    workDays,
    holidays,
    vacationDaysTaken,
    sickDays,
    averageHoursPerDay: round(averageHoursPerDay, 2),
    averageWorkDaysPerWeek: round(averageWorkDaysPerWeek, 2),
    vacationEntitlementRaw: round(vacationEntitlementProRated, 2),
    vacationEntitlementRounded,
    carryOverFromPreviousYear,
    totalVacationEntitlement,
    remainingVacation,
    last13Weeks,
    vacationEarnedToDate,
    // Teilurlaub-Felder (nur gesetzt wenn proRataFactor < 1)
    ...(proRataFactor < 1 ? {
      fullEmploymentMonths,
      proRataFactor: round(proRataFactor, 4),
      vacationEntitlementFullYear,
    } : {}),
  };
}

/**
 * Berechnet die Durchschnittswerte der letzten 13 Wochen
 * für die Urlaubsentgelt-Berechnung nach §11 BUrlG
 *
 * Berücksichtigt auch Daten aus dem Vorjahr, falls der 13-Wochen-Zeitraum
 * ins Vorjahr hineinreicht (z.B. bei Urlaub im Januar/Februar/März)
 *
 * Schließt Urlaub, Krankheit, Feiertage und Betriebsferien aus
 */
export function calculateLast13Weeks(
  entries: Record<string, DayEntry>,
  settings: CompanySettings,
  referenceDate: Date,
  previousYearEntries?: Record<string, DayEntry>
): Last13WeeksData {
  const startDate = startOfDay(subDays(referenceDate, 91)); // 13 Wochen = 91 Tage
  const endDate = startOfDay(referenceDate);

  // Kombiniere aktuelle und Vorjahres-Einträge für die Berechnung
  const allEntries: DayEntry[] = [
    ...Object.values(entries),
    ...(previousYearEntries ? Object.values(previousYearEntries) : []),
  ];

  // Filtere: nur reguläre Arbeitstage (keine Urlaub/Krank/Feiertage/Betriebsferien)
  const relevantEntries = allEntries.filter((entry) => {
    const entryDate = parseISO(entry.date);
    return (
      isWithinInterval(entryDate, { start: startDate, end: endDate }) &&
      entry.hours > 0 &&
      !entry.isVacation &&
      !entry.isHalfVacation &&
      !entry.isHoliday &&
      !entry.isSickDay &&
      !entry.isCompanyVacation
    );
  });

  const totalHours = relevantEntries.reduce((sum, e) => sum + e.hours, 0);
  const workDays = relevantEntries.length;
  const averageHoursPerDay = workDays > 0 ? totalHours / workDays : 0;
  const vacationPayPerDay = averageHoursPerDay * (settings.hourlyWage ?? 0);

  return {
    totalHours: round(totalHours, 2),
    workDays,
    averageHoursPerDay: round(averageHoursPerDay, 2),
    vacationPayPerDay: round(vacationPayPerDay, 2),
  };
}

/**
 * Berechnet die durchschnittliche Arbeitszeit für einen bestimmten Wochentag
 * aus den letzten 13 Wochen (91 Tage) vor dem Zieldatum.
 * Wird für die automatische Stundenberechnung bei Krankheit verwendet.
 *
 * @param entries - Alle Einträge des aktuellen Jahres
 * @param targetDate - Das Datum, für das der Durchschnitt berechnet werden soll (bestimmt den Wochentag)
 * @param previousYearEntries - Optionale Einträge des Vorjahres
 * @param referenceDate - Optionales Referenzdatum für das 91-Tage-Fenster (§11 BUrlG: erster Tag des Urlaubsblocks).
 *                        Falls nicht gesetzt, wird targetDate verwendet.
 * @returns Durchschnittliche Stunden für diesen Wochentag (0 wenn keine Daten)
 */
export function calculateAverageHoursForWeekday(
  entries: Record<string, DayEntry>,
  targetDate: Date,
  previousYearEntries?: Record<string, DayEntry>,
  referenceDate?: Date
): number {
  const dayOfWeek = targetDate.getDay(); // 0=Sonntag, 1=Montag, ...
  const windowAnchor = referenceDate ?? targetDate;
  const startDate = startOfDay(subDays(windowAnchor, 91)); // 13 Wochen zurück

  // Alle Einträge kombinieren
  const allEntries: DayEntry[] = [
    ...Object.values(entries),
    ...(previousYearEntries ? Object.values(previousYearEntries) : []),
  ];

  // Nur Einträge vom gleichen Wochentag in den letzten 13 Wochen
  // Ausschluss: Urlaub, Feiertage, Krankheit, Betriebsferien
  const relevantEntries = allEntries.filter((entry) => {
    const entryDate = parseISO(entry.date);
    return (
      entryDate.getDay() === dayOfWeek &&
      isWithinInterval(entryDate, { start: startDate, end: windowAnchor }) &&
      entry.hours > 0 &&
      !entry.isVacation &&
      !entry.isHalfVacation &&
      !entry.isHoliday &&
      !entry.isSickDay &&
      !entry.isCompanyVacation
    );
  });

  if (relevantEntries.length === 0) {
    return 0;
  }

  const totalHours = relevantEntries.reduce((sum, e) => sum + e.hours, 0);
  return round(totalHours / relevantEntries.length, 2);
}

/**
 * Berechnet monatliche Zusammenfassungen (Kalendermonat: 1. bis letzter Tag)
 */
export function calculateMonthlyData(
  entries: Record<string, DayEntry>,
  year: number
): MonthlyData[] {
  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;
    const monthStr = month.toString().padStart(2, '0');
    const prefix = `${year}-${monthStr}`;

    const monthEntries = Object.values(entries).filter((e) =>
      e.date.startsWith(prefix)
    );

    return {
      month,
      year,
      monthName: getMonthName(i),
      totalHours: round(
        monthEntries.reduce((sum, e) => sum + e.hours, 0),
        2
      ),
      workDays: monthEntries.filter((e) => e.hours > 0).length,
      holidays: monthEntries.filter((e) => e.isHoliday).length,
      vacationDays: monthEntries.reduce((sum, e) => {
        if (e.isVacation) return sum + 1;
        if (e.isHalfVacation) return sum + 0.5;
        return sum;
      }, 0),
      sickDays: monthEntries.filter((e) => e.isSickDay).length,
    };
  });
}

/**
 * Berechnet monatliche Zusammenfassungen für Abrechnungszeitraum
 * (21. des Vormonats bis 20. des aktuellen Monats)
 */
export function calculatePayrollMonthlyData(
  entries: Record<string, DayEntry>,
  year: number,
  previousYearEntries?: Record<string, DayEntry>
): MonthlyData[] {
  // §11 BUrlG vor dem Summieren anwenden. Diese Funktion speist die
  // HR-Monatsübersicht, die Mitarbeiter-Ansicht und damit die Zahl, die ans
  // Lohnbüro gemeldet wird. Ohne die Neuberechnung zählen block-gebuchte
  // Urlaubstage mit ihren gespeicherten 0 Stunden — genau so sind für den
  // Abrechnungszeitraum August 2026 statt 82,25 nur 68,50 Std. gemeldet worden.
  //
  // Das Vorjahr wird separat neu berechnet, weil der Januar-Zeitraum am 21.12.
  // beginnt und dessen Urlaubstage sonst weiter mit 0 einflössen.
  const recalculated = applyVacationRecalc(entries, previousYearEntries);
  const recalculatedPrevious = previousYearEntries
    ? applyVacationRecalc(previousYearEntries)
    : undefined;

  const allEntries = {
    ...recalculatedPrevious,
    ...recalculated,
  };

  return Array.from({ length: 12 }, (_, i) => {
    const month = i + 1;

    // Berechne Start- und Enddatum des Abrechnungszeitraums
    // Start: 21. des Vormonats
    // Ende: 20. des aktuellen Monats
    let startYear = year;
    let startMonth = month - 1;

    if (startMonth === 0) {
      startMonth = 12;
      startYear = year - 1;
    }

    const startDate = `${startYear}-${startMonth.toString().padStart(2, '0')}-21`;
    const endDate = `${year}-${month.toString().padStart(2, '0')}-20`;

    // Filtere Einträge im Zeitraum
    const monthEntries = Object.values(allEntries).filter((e) => {
      return e.date >= startDate && e.date <= endDate;
    });

    return {
      month,
      year,
      monthName: getMonthName(i),
      totalHours: round(
        monthEntries.reduce((sum, e) => sum + e.hours, 0),
        2
      ),
      workDays: monthEntries.filter((e) => e.hours > 0).length,
      holidays: monthEntries.filter((e) => e.isHoliday).length,
      vacationDays: monthEntries.reduce((sum, e) => {
        if (e.isVacation) return sum + 1;
        if (e.isHalfVacation) return sum + 0.5;
        return sum;
      }, 0),
      sickDays: monthEntries.filter((e) => e.isSickDay).length,
    };
  });
}

/**
 * Berechnet den Urlaubsanspruch bei Jahresarbeitszeitbasis
 * (für stark unregelmäßige Arbeitszeiten)
 */
export function calculateVacationByYearlyWorkdays(
  vacationDaysFullTime: number,
  yearlyWorkDaysFullTime: number,
  yearlyWorkDaysEmployee: number
): { raw: number; rounded: number } {
  const raw = (vacationDaysFullTime / yearlyWorkDaysFullTime) * yearlyWorkDaysEmployee;
  return {
    raw: round(raw, 2),
    rounded: roundVacationDays(raw),
  };
}

/**
 * Standardformel für Urlaubsanspruch bei fester Wochenarbeitszeit
 */
export function calculateVacationByWeeklyWorkdays(
  vacationDaysFullTime: number,
  workDaysPerWeekFullTime: number,
  workDaysPerWeekEmployee: number
): { raw: number; rounded: number } {
  const raw = (vacationDaysFullTime / workDaysPerWeekFullTime) * workDaysPerWeekEmployee;
  return {
    raw: round(raw, 2),
    rounded: roundVacationDays(raw),
  };
}

// ─── Dynamische Urlaubsstunden-Neuberechnung (§11 BUrlG) ───────────────────

/**
 * Ergebnis der dynamischen Urlaubsstunden-Neuberechnung für einen einzelnen Tag.
 */
export interface VacationRecalcEntry {
  /** Berechnete Urlaubsstunden basierend auf aktuellem 13-Wochen-Durchschnitt */
  hours: number;
  /** true = Tag liegt in der Zukunft, Wert ist vorläufig */
  isProvisional: boolean;
  /** false = keine Arbeitsdaten im 13-Wochen-Lookback vorhanden */
  hasData: boolean;
}

/**
 * Gruppiert Urlaubstage in zusammenhängende Blöcke.
 * Ein Block ist eine Folge von Urlaubstagen, bei der Lücken von bis zu 3 Kalendertagen
 * (Wochenenden) den Block nicht unterbrechen — konsistent mit findVacationReferenceDate.
 *
 * @param entries - Alle Einträge des Jahres
 * @returns Array von Blöcken, jeweils mit Referenzdatum (erster Tag) und allen Datums-Strings
 */
export function groupVacationBlocks(
  entries: Record<string, DayEntry>
): Array<{ referenceDate: string; dates: string[] }> {
  const vacationDates = Object.values(entries)
    .filter((e) => e.isVacation || e.isHalfVacation)
    .map((e) => e.date)
    .sort();

  if (vacationDates.length === 0) return [];

  const blocks: Array<{ referenceDate: string; dates: string[] }> = [];
  let currentBlock: string[] = [vacationDates[0]!];

  for (let i = 1; i < vacationDates.length; i++) {
    const prev = parseISO(vacationDates[i - 1]!);
    const curr = parseISO(vacationDates[i]!);
    const diff = differenceInCalendarDays(curr, prev);

    if (diff <= 3) {
      currentBlock.push(vacationDates[i]!);
    } else {
      blocks.push({ referenceDate: currentBlock[0]!, dates: [...currentBlock] });
      currentBlock = [vacationDates[i]!];
    }
  }
  blocks.push({ referenceDate: currentBlock[0]!, dates: [...currentBlock] });

  return blocks;
}

/**
 * Berechnet dynamisch die vacationCalculatedHours für alle zukünftigen Urlaubstage.
 * Vergangene Urlaubstage bleiben unberührt (ihr DB-Wert ist final).
 *
 * Für jeden Urlaubsblock wird das Referenzdatum (§11 BUrlG: erster Tag des Blocks)
 * als Anker für das 91-Tage-Fenster verwendet. Die Wochentags-spezifische Berechnung
 * (calculateAverageHoursForWeekday) liefert den Durchschnitt pro Wochentag.
 *
 * @param entries - Alle Einträge des aktuellen Jahres
 * @param previousYearEntries - Optionale Einträge des Vorjahres
 * @param today - Optionales heutiges Datum (für Tests)
 * @returns Map von Datums-String → VacationRecalcEntry für alle zukünftigen Urlaubstage
 */
export function recalcFutureVacationHours(
  entries: Record<string, DayEntry>,
  previousYearEntries?: Record<string, DayEntry>,
  today?: Date
): Map<string, VacationRecalcEntry> {
  const result = new Map<string, VacationRecalcEntry>();
  const todayDate = startOfDay(today ?? new Date());
  const blocks = groupVacationBlocks(entries);

  for (const block of blocks) {
    const hasFutureDays = block.dates.some((d) => parseISO(d) > todayDate);
    // Vergangene Tage ebenfalls berechnen wenn Stunden noch nicht finalisiert.
    // `undefined` = noch nicht berechnet. Eine finalisierte 0 (z. B. Halbtag
    // oder Sonderfall) darf NICHT erneut überschrieben werden.
    const hasUnfinalizedPastDays = block.dates.some((d) => {
      const e = entries[d];
      return parseISO(d) <= todayDate && e?.vacationCalculatedHours === undefined;
    });
    if (!hasFutureDays && !hasUnfinalizedPastDays) continue;

    const refDate = parseISO(block.referenceDate);

    for (const dateStr of block.dates) {
      const dayDate = parseISO(dateStr);
      const entry = entries[dateStr];
      // Vergangene Tage überspringen, wenn Stunden bereits finalisiert.
      if (dayDate <= todayDate && entry?.vacationCalculatedHours !== undefined) continue;

      const hours = calculateAverageHoursForWeekday(
        entries,
        dayDate,
        previousYearEntries,
        refDate
      );

      // Halbe Urlaubstage: hours halbieren (konsistent mit DayEditModal-Logik)
      const effectiveHours = entry?.isHalfVacation ? round(hours / 2, 2) : hours;

      result.set(dateStr, {
        hours: effectiveHours,
        isProvisional: dayDate > todayDate,
        hasData: hours > 0,
      });
    }
  }

  return result;
}

/**
 * Legt eine bereits berechnete Urlaubsstunden-Map über die Einträge.
 *
 * Hintergrund: block-gebuchte Urlaubstage liegen mit `hours = 0` in der
 * Datenbank, weil das Urlaubsentgelt nach §11 BUrlG erst zum Urlaubsantritt
 * feststeht. Jeder Pfad, der Stunden anzeigt oder meldet, muss diese
 * Neuberechnung anwenden — sonst summiert er die Nullen.
 *
 * Tage ohne Arbeitsdatum im 91-Tage-Fenster (`hasData === false`) bleiben
 * unangetastet: dort ist die 0 kein Rechenergebnis, sondern eine fehlende
 * Grundlage. Sie als Wert zu übernehmen würde einen unfinalisierten Tag
 * dauerhaft auf null festschreiben.
 */
export function applyVacationRecalcMap(
  entries: Record<string, DayEntry>,
  recalc: Map<string, VacationRecalcEntry>
): Record<string, DayEntry> {
  if (recalc.size === 0) return entries;

  const result = { ...entries };
  for (const [dateStr, computed] of recalc) {
    const entry = result[dateStr];
    if (!entry || !computed.hasData) continue;
    result[dateStr] = {
      ...entry,
      hours: computed.hours,
      vacationCalculatedHours: computed.hours,
    };
  }
  return result;
}

/**
 * Berechnet die Urlaubsstunden nach §11 BUrlG neu und legt sie über die
 * Einträge. Bequemlichkeits-Wrapper um `recalcFutureVacationHours` +
 * `applyVacationRecalcMap` für alle Aufrufer, die die Map selbst nicht brauchen.
 */
export function applyVacationRecalc(
  entries: Record<string, DayEntry>,
  previousYearEntries?: Record<string, DayEntry>,
  today?: Date
): Record<string, DayEntry> {
  return applyVacationRecalcMap(
    entries,
    recalcFutureVacationHours(entries, previousYearEntries, today)
  );
}

/**
 * Hilfsfunktion zum Runden auf n Dezimalstellen.
 *
 * Exportiert, damit Aufrufer, die Werte für dieselben Felder berechnen (etwa
 * der Tagesdialog für halbe Urlaubstage), identisch runden wie
 * `recalcFutureVacationHours` — sonst weichen die Werte um Cent-Beträge ab.
 */
export function round(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Prüft ob eine Stundenanzahl gültig ist
 */
export function isValidHours(hours: number): boolean {
  return hours >= 0 && hours <= 24 && Number.isFinite(hours);
}

/**
 * Formatiert einen Zahlenwert für die deutsche Anzeige
 */
export function formatNumber(value: number | null | undefined, decimals: number = 2): string {
  return (value ?? 0).toLocaleString('de-DE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formatiert einen Eurobetrag
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value == null) {
    return '—';
  }
  return value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}
